/**
 * Transaction History Module - MIGRATED TO REUSABLE WAREHOUSE TABLE
 *
 * This demonstrates the new TransactionHistory implementation using the reusable
 * WarehouseTable system, showing 82% code reduction (987 lines → 150 lines)
 * while maintaining identical functionality and appearance.
 *
 * Features maintained:
 * - Period filter (DateRangeFilter: quick presets, Fra/Til, optional time) with API calls
 * - Transaction type badges with color coding
 * - Excel-like column configuration and filtering
 * - Wildcard search patterns with % support
 * - Column sorting, resizing, and reordering
 * - Summary calculations (total quantity)
 * - CSV export functionality
 * - All original styling and behavior preserved
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  History,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { transactionHistoryService, TransactionFilterOptions } from '../../../../services/transactionHistoryService';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';
import { warehouseService } from '../../../../services/warehouseService';
import { statusCodesService } from '../../../../services/statusCodesService';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import {
  StcTransactionDto
} from '../../../../types/transactionHistory';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

// Import the new reusable warehouse table system
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, SummaryCalculation, ServerFetchParams } from '../../../shared/table/types/table';
import { BackButton } from '../../../common/BackButton';
import { DateRangeFilter } from '../../../common/DateRangeFilter';
import {
  DateRangeValue,
  parseStoredDateRange,
  resolveDateRange,
  validateDateRange
} from '../../../../utils/dateRange';

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<StcTransactionDto>();

// Periodevalget huskes per tenant og operatør i nettleseren (GUI-valg, ikke en innstilling)
const periodStorageKey = (customerNo?: string, operatorId?: string) =>
  `transactionHistory.period:${customerNo || ''}:${operatorId || ''}`;

const readStoredPeriod = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const timeStr = date.toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${dateStr} ${timeStr}`;
};

const formatNumber = (num: number): string => {
  return num.toLocaleString('no-NO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

interface TransactionHistoryAppProps {
  onBack?: () => void;
}

export const TransactionHistoryApp: React.FC<TransactionHistoryAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // B3.1: TransType er StatusCodes-oppslag (StatusType 27). Autoritativ kilde for tekst er
  // StatusCodes (tenant-overstyrbar, dekker alle gyldige koder inkl. f.eks. 17 Internflytt).
  const [transTypeMap, setTransTypeMap] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    statusCodesService.getByStatusType(27)
      .then(codes => setTransTypeMap(new Map(codes.map(sc => [sc.statusCode, sc.lng_1]))))
      .catch(() => setTransTypeMap(new Map()));
  }, []);

  // Språk-bevisste, KODE-baserte filteropsjoner for Typ-kolonnen (StatusType 27).
  // Filterverdi = kode (språknøytral) → backend filtrerer WHERE TransType IN (koder).
  const transTypeOptions = useStatusOptions(27);

  // Transaction-type label by numeric code. Prioritet: StatusCodes (StatusType 27) →
  // app-i18n (flerspråk-fallback) → server-levert fallback for ukjente koder.
  const getTransTypeLabel = useCallback((transType: number, fallback: string): string => {
    const fromStatusCodes = transTypeMap.get(transType);
    if (fromStatusCodes) return fromStatusCodes;

    const i18nLabels: Record<number, string> = {
      10: t('manager.modules.transactionHistory.transTypes.10', {}, 'Plukkliste'),
      11: t('manager.modules.transactionHistory.transTypes.11', {}, 'Lageroverføring'),
      12: t('manager.modules.transactionHistory.transTypes.12', {}, 'Utlegg'),
      13: t('manager.modules.transactionHistory.transTypes.13', {}, 'Påfylling'),
      14: t('manager.modules.transactionHistory.transTypes.14', {}, 'Optimalisering'),
      15: t('manager.modules.transactionHistory.transTypes.15', {}, 'Korreksjon'),
      17: t('manager.modules.transactionHistory.transTypes.17', {}, 'Internflytt'),
      20: t('manager.modules.transactionHistory.transTypes.20', {}, 'Varemottak'),
      88: t('manager.modules.transactionHistory.transTypes.88', {}, 'Inngående saldo'),
      89: t('manager.modules.transactionHistory.transTypes.89', {}, 'Saldoimport ERP'),
      90: t('manager.modules.transactionHistory.transTypes.90', {}, 'Saldoavstemming ERP'),
      100: t('manager.modules.transactionHistory.transTypes.100', {}, 'Telling')
    };
    return i18nLabels[transType] ?? fallback;
  }, [t, transTypeMap]);

  // State management
  const [transactions, setTransactions] = useState<StcTransactionDto[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true); // Start true for initial load spinner
  const [error, setError] = useState<string | null>(null);

  // Perioden: hurtigvalg eller egendefinert Fra/Til. Hentes ved hver endring (når gyldig).
  // Hurtigvalg regnes fra dagens dato ved hver henting, så «I dag» følger med over midnatt.
  const storageKey = periodStorageKey(user?.customerNo, user?.operatorId);
  const [period, setPeriod] = useState<DateRangeValue>(() => parseStoredDateRange(readStoredPeriod(storageKey)));
  const periodRef = useRef(period);
  const periodDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Server pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const lastFetchRef = useRef<{
    page: number;
    size: number;
    sortColumn?: string;
    sortDirection?: string;
    filters?: Record<string, string>;
    dropdownFilters?: Record<string, string[]>;
  }>({ page: 1, size: 50 });
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDoneRef = useRef(false);
  const fetchIdRef = useRef(0); // Monotonic counter to discard stale API responses

  // Dropdown filter options from all data in date range (not just current page)
  const [filterOptions, setFilterOptions] = useState<TransactionFilterOptions | null>(null);

  // Lager-navn (kode → beskrivelse) for B3 «kode - tekst»-visning på Lager-kolonnen
  const [warehouseMap, setWarehouseMap] = useState<Map<string, string>>(new Map());

  // Operatørnavn (UserID → ProfileName) — kunden skal se navn, ikke ID (TW 36353248).
  // Verdien i raden er fortsatt UserID, så sortering og server-filtrering er uberørt.
  const { getDisplayName } = useOperatorDisplayNames();

  useEffect(() => {
    warehouseService.getWarehouses()
      .then(list => setWarehouseMap(new Map(list.map(w => [String(w.warehouseID), w.warehouseDescr]))))
      .catch(() => setWarehouseMap(new Map()));
  }, []);

  /**
   * Define columns using the type-safe column factory
   */
  const columns: ColumnDefinition<StcTransactionDto>[] = useMemo(() => [
    columnFactory.createDateColumn('transDate', 'transDate', t('common.fields.date', {}, 'Dato'), {
      width: 140,
      sortable: true,
      filterType: 'none',
      hideFilter: true, // perioden velges øverst (DateRangeFilter), ikke i filterraden
      formatter: (value: string) => formatDateTime(value)
    }),
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 150,
      sortable: true,
      filterType: 'both', // oppslagstabell — flervalgs-dropdown (verdier fra filterOptions)
      // B3: vis «kode - navn» (filtrering skjer fortsatt på koden)
      formatter: (value: any) => {
        if (value === null || value === undefined || value === '') return String(value || '-');
        const name = warehouseMap.get(String(value));
        return name ? `${value} - ${name}` : String(value);
      }
    }),
    columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createCustomColumn({
      id: 'transTypeDescription',
      key: 'transTypeDescription',
      label: t('common.fields.type', {}, 'Type'),
      width: 140,
      visible: true,
      sortable: true,
      resizable: true,
      minWidth: 100,
      alignment: 'left',
      type: 'text',
      filterType: 'dropdown',
      // Kode-basert, språk-bevisst filter (filterOptions vinner over kildeliste). Verdi = kode.
      filterOptions: transTypeOptions.map(o => ({ value: String(o.value), label: o.label })),
      // B3.1: Type er StatusCodes-oppslag (StatusType 27). «kode - tekst» i dropdown/eksport via
      // formatter. Returner '' for tomme/syntetiske rader (transType 0) så de IKKE blir en «-»-verdi
      // i Type-dropdownen (de syntetiske Lager-radene deler datasett og har tom transTypeDescription).
      formatter: (value: any, row: StcTransactionDto) =>
        row.transType != null && row.transType !== 0
          ? `${row.transType} - ${getTransTypeLabel(row.transType, String(value || ''))}`
          : '',
      // Enhetlig visning via StatusBadge (StatusType 27, språk-bevisst «kode - tekst» + farge).
      // Filter/eksport bruker fortsatt getTransTypeLabel (formatter over).
      renderer: (_value: string, row: StcTransactionDto) =>
        row.transType != null && row.transType !== 0
          ? <StatusBadge value={row.transType} statusType={27} />
          : <span className="text-gray-400">-</span>
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.transactionHistory.columns.itemNo', {}, 'Varenr.'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('itemDescription', 'itemDescription', t('manager.modules.transactionHistory.columns.itemDescription', {}, 'Varebeskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', t('common.fields.quantity', {}, 'Antall'), {
      width: 80,
      sortable: true,
      filterType: 'text',
      formatter: (value: number) => formatNumber(value)
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', t('manager.modules.transactionHistory.columns.operator', {}, 'Operatør'), {
      width: 150,
      sortable: true,
      // «both» gir både fritekst på koden og flervalgs-dropdown fra filterOptions.
      // Uten dropdownen ville et søk på navnet ikke truffet noe, siden server-filteret
      // matcher operatorID. Dropdownen viser navnet (via formatteren) og sender koden.
      filterType: 'both',
      formatter: (value: any) => {
        if (value === null || value === undefined || value === '') return '-';
        return getDisplayName(String(value));
      }
    }),
    columnFactory.createTextColumn('serialNo', 'serialNo', t('manager.modules.transactionHistory.columns.serialNo', {}, 'Serienr.'), {
      width: 110,
      sortable: true,
      filterType: 'text' // frie verdier rett på StcTransaction — ingen lookup-tabell
    }),
    columnFactory.createTextColumn('batchNo', 'batchNo', t('manager.modules.transactionHistory.columns.batchNo', {}, 'Batchnr.'), {
      width: 100,
      sortable: true,
      filterType: 'text' // frie verdier rett på StcTransaction — ingen lookup-tabell
    }),
    columnFactory.createDateColumn('expiryDate', 'expiryDate', t('manager.modules.transactionHistory.columns.expiryDate', {}, 'Utløpsdato'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.transactionHistory.columns.orderNoERP', {}, 'Ordre (ERP)'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', t('manager.modules.transactionHistory.columns.projectNo', {}, 'Prosjektnr'), {
      width: 110,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('depNo', 'depNo', t('manager.modules.transactionHistory.columns.depNo', {}, 'Avdeling'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('lineNoERP', 'lineNoERP', t('manager.modules.transactionHistory.columns.lineNoERP', {}, 'Linje (ERP)'), {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('orderName', 'orderName', t('manager.modules.transactionHistory.columns.orderName', {}, 'Kunde/Lev.'), {
      width: 150,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('pListID', 'pListID', t('manager.modules.transactionHistory.columns.pListID', {}, 'Plukkliste'), {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('pListLineNo', 'pListLineNo', t('manager.modules.transactionHistory.columns.pListLineNo', {}, 'Plukklinje'), {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('grShipmentNo', 'grShipmentNo', t('manager.modules.transactionHistory.columns.grShipmentNo', {}, 'Partinr.'), {
      width: 80,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('reasonDescription', 'reasonDescription', t('manager.modules.transactionHistory.columns.reasonDescription', {}, 'Årsak'), {
      width: 150,
      sortable: true,
      filterType: 'text'
    })
    // getDisplayName MÅ være med i avhengighetslista under: profillista lastes
    // asynkront, og uten den ville kolonnene blitt memoisert med et tomt
    // navneoppslag og fortsatt vist operatør-ID etter at navnene kom inn.
  ], [t, getTransTypeLabel, warehouseMap, transTypeOptions, getDisplayName]);

  /**
   * Define summary calculations
   */
  const summaryCalculations: SummaryCalculation<StcTransactionDto>[] = useMemo(() => [
    {
      column: 'quantity',
      type: 'sum',
      formatter: (value: number) =>
        t('manager.modules.transactionHistory.summary.total', { '0': formatNumber(value) }, `Total: ${formatNumber(value)}`)
    }
  ], [t]);

  // Fetch all distinct filter values for dropdowns (across entire date range)
  const loadFilterOptions = useCallback(async () => {
    try {
      const { from, to } = resolveDateRange(periodRef.current);
      const options = await transactionHistoryService.getFilterOptions(from, to);
      setFilterOptions(options);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, []);

  // Build synthetic data rows so ColumnDropdownFilter shows ALL distinct values (across hele
  // datointervallet, ikke bare gjeldende side). Hver rad bærer verdien for én dropdown-kolonne;
  // tomme felt hoppes over per kolonne (formattedValue.trim() === '' filtreres bort).
  const dropdownOptionsData: StcTransactionDto[] = useMemo(() => {
    const emptyRow = {
      id: -1, companyNo: '', warehouseID: '', transDate: '', transType: 0,
      transTypeDescription: '', itemNo: '', itemDescription: '', binCode: '',
      quantity: 0, operatorID: '', orderNo: null, orderNoERP: null, projectNo: null,
      depNo: null, lineNo: null, lineNoERP: null, orderName: null, pListID: null,
      pListLineNo: null, grShipmentNo: null, reasonDescription: null, clientID: null,
      created: null, createdBy: null,
    } as unknown as StcTransactionDto;

    // Type-rader fra StatusCodes (StatusType 27) — dekker alle gyldige typer i kildelista.
    // Filteret er kode-basert (eksplisitt filterOptions vinner, verdi=kode; backend matcher
    // st.TransType IN (koder)); transTypeDescription her er kun kildeliste-tekst, formatteren viser «kode - tekst».
    const typeRows = Array.from(transTypeMap.entries()).map(([code, lng]) => ({
      ...emptyRow,
      transType: code,
      transTypeDescription: lng,
    } as StcTransactionDto));

    const warehouseRows = (filterOptions?.warehouseIds || []).map(wid => ({
      ...emptyRow,
      warehouseID: wid,
    } as StcTransactionDto));

    // Operatør-rader gir dropdownen en komplett liste over operatørene i perioden.
    // Verdien er UserID; formatteren gjør den om til ProfileName i visningen.
    const operatorRows = (filterOptions?.operatorIds || []).map(oid => ({
      ...emptyRow,
      operatorID: oid,
    } as StcTransactionDto));

    return [...typeRows, ...warehouseRows, ...operatorRows];
  }, [transTypeMap, filterOptions]);

  // Load transactions with server-side pagination, sorting and filtering
  const loadTransactions = useCallback(async (
    page: number = currentPage,
    size: number = pageSize,
    sortColumn?: string,
    sortDirection?: string,
    filters?: Record<string, string>,
    dropdownFilters?: Record<string, string[]>
  ) => {
    // Only show full loading spinner on initial load — subsequent fetches keep table visible
    // (same pattern as ItemsDataTable)
    if (!initialLoadDoneRef.current) {
      setLoading(true);
    }
    setError(null);

    // Increment request counter — if a newer request starts before this one finishes,
    // discard this response to prevent stale data from overwriting fresher results
    const thisRequestId = ++fetchIdRef.current;

    try {
      // Send column filters and dropdown filters to service — it handles key mapping to API params
      const { from, to } = resolveDateRange(periodRef.current);
      const result = await transactionHistoryService.getTransactionHistoryPaged(
        from,
        to,
        page,
        size,
        filters,
        dropdownFilters,
        sortColumn,
        sortDirection
      );

      // Discard stale response — a newer request has been issued since this one started
      if (thisRequestId !== fetchIdRef.current) {
        return;
      }

      setTransactions(result.items);
      setTotalRecords(result.totalRecords);
      setCurrentPage(page);
      setPageSize(size);
      lastFetchRef.current = { page, size, sortColumn, sortDirection, filters, dropdownFilters };
      initialLoadDoneRef.current = true;
    } catch (err) {
      // Only show error if this is still the latest request
      if (thisRequestId === fetchIdRef.current) {
        setError(err instanceof Error ? err.message : t('manager.modules.transactionHistory.errors.unknown', {}, 'En ukjent feil oppstod'));
      }
    } finally {
      if (thisRequestId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentPage, pageSize, t]);

  // Handle server pagination/sort/filter callback from WarehouseTable
  // Filter changes are debounced 300ms to prevent flickering while typing
  const handleFetchData = useCallback((params: ServerFetchParams) => {
    const { pageNumber, pageSize: newSize, sortColumn, sortDirection, filters, dropdownFilters } = params;
    const last = lastFetchRef.current;

    // Avoid duplicate fetches for identical params
    const isSame = pageNumber === last.page
      && newSize === last.size
      && sortColumn === last.sortColumn
      && sortDirection === last.sortDirection
      && JSON.stringify(filters) === JSON.stringify(last.filters)
      && JSON.stringify(dropdownFilters) === JSON.stringify(last.dropdownFilters);

    if (isSame) {
      return;
    }

    // Clear any pending debounce
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = null;
    }

    // Detect if this is a text filter change (debounce to avoid flicker while typing)
    const isTextFilterChange = pageNumber === 1
      && JSON.stringify(filters) !== JSON.stringify(last.filters);

    // Dropdown filter changes should execute immediately (discrete selection, not typing)
    const isDropdownChange = JSON.stringify(dropdownFilters) !== JSON.stringify(last.dropdownFilters);

    if (isTextFilterChange && !isDropdownChange && initialLoadDoneRef.current) {
      // Debounce text filter changes only
      filterDebounceRef.current = setTimeout(() => {
        loadTransactions(pageNumber, newSize, sortColumn, sortDirection, filters, dropdownFilters);
      }, 300);
    } else {
      // Page, sort, dropdown changes execute immediately
      loadTransactions(pageNumber, newSize, sortColumn, sortDirection, filters, dropdownFilters);
    }
  }, [loadTransactions]);

  // Load on mount
  useEffect(() => {
    loadTransactions(1, 50);
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Henter side 1 for gjeldende periode og beholder sortering og kolonnefiltre
  const reloadForPeriod = useCallback(() => {
    const last = lastFetchRef.current;
    loadTransactions(1, pageSize, last.sortColumn, last.sortDirection, last.filters, last.dropdownFilters);
    loadFilterOptions(); // nedtrekkslistene følger perioden
  }, [loadTransactions, loadFilterOptions, pageSize]);

  // Ny periode: lagre valget og hent på nytt. Kort forsinkelse så et datofelt som skrives
  // tegn for tegn ikke gir én henting per tastetrykk; ugyldig periode hentes ikke.
  const handlePeriodChange = (next: DateRangeValue) => {
    setPeriod(next);
    if (periodDebounceRef.current) {
      clearTimeout(periodDebounceRef.current);
      periodDebounceRef.current = null;
    }
    if (validateDateRange(next) !== null) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Privat modus o.l. — perioden gjelder likevel i denne visningen
    }
    periodDebounceRef.current = setTimeout(() => {
      periodRef.current = next;
      reloadForPeriod();
    }, 400);
  };

  useEffect(() => () => {
    if (periodDebounceRef.current) clearTimeout(periodDebounceRef.current);
  }, []);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header with integrated filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <History className="w-5 h-5 text-teal-600" />
            <h1 className="text-lg font-semibold text-gray-900">{t('manager.modules.transactionHistory.title', {}, 'Transaksjonshistorikk')}</h1>
            {loading && <Loader2 className="w-4 h-4 text-teal-600 animate-spin" />}
          </div>
        </div>
        {/* Periode: hurtigvalg, Fra/Til og valgfritt klokkeslett. Henter ved endring. */}
        <div className="flex items-center justify-between gap-4 mt-3">
          <DateRangeFilter value={period} onChange={handlePeriodChange} />
          <button
            type="button"
            onClick={reloadForPeriod}
            disabled={loading || validateDateRange(period) !== null}
            title={t('manager.modules.transactionHistory.buttons.refreshTitle', {}, 'Hent på nytt — tar med nye transaksjoner')}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="w-4 h-4" />
            {t('manager.modules.transactionHistory.buttons.refresh', {}, 'Oppdater')}
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">{t('manager.modules.transactionHistory.errors.loadFailed', {}, 'Feil ved lasting av data')}</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={() => loadTransactions(currentPage, pageSize)}
                className="mt-3 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                {t('manager.modules.transactionHistory.buttons.retry', {}, 'Prøv igjen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - WarehouseTable */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={transactions}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false, // Date filtering handled separately
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'transaction-history'
          }}
          serverPagination={{
            enabled: true,
            totalRecords: totalRecords,
            onFetchData: handleFetchData
          }}
          dropdownOptionsData={dropdownOptionsData.length > 0 ? dropdownOptionsData : undefined}
          enableDropdownFilters={true}
          summaryCalculations={summaryCalculations}
          loading={loading}
          error={error || undefined}
        />
      </div>
    </div>
  );
};